import React, { useState, useEffect, useMemo } from 'react';
import {
    Box, Typography, Button, Divider, Grid, Card, CardContent, Container, Table, TableBody, TableCell,
    TableContainer, TableHead, TableRow, Dialog, DialogTitle, DialogContent, DialogActions, TextField,
    IconButton, Tooltip, Snackbar, Alert, CircularProgress, useTheme, Stack, InputAdornment, MenuItem, Select, FormControl, InputLabel, useMediaQuery
} from '@mui/material';
import {
    Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon, Close as CloseIcon, Save as SaveIcon,
    Search as SearchIcon, ArrowUpward, ArrowDownward, UnfoldMore
} from '@mui/icons-material';
import { ThemeProvider } from '@mui/material/styles';
import api from '../../../services/api';
import Paginate from '../../../components/Paginate';

const itemsPerPage = 6;

const SubsectorPage = () => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const [subsectors, setSubsectors] = useState([]);
    const [sectors, setSectors] = useState([]);
    const [loading, setLoading] = useState(true);
    const [openDialog, setOpenDialog] = useState(false);
    const [currentSubsector, setCurrentSubsector] = useState(null);
    const [formData, setFormData] = useState({
        name: '',
        code: '',
        sectorActivitId: null
    });
    const [initialFormData, setInitialFormData] = useState({
        name: '',
        code: '',
        sectorActivitId: null
    });
    const [snackbar, setSnackbar] = useState({
        open: false,
        message: '',
        severity: 'success'
    });
    const [page, setPage] = useState(1);
    const [openConfirmDialog, setOpenConfirmDialog] = useState(false);
    const [subsectorToDeleteId, setSubsectorToDeleteId] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [sortConfig, setSortConfig] = useState({
        key: null,
        direction: 'ascending',
    });

    useEffect(() => {
        fetchSectors();
        fetchSubsectors();
    }, []);

    const fetchSectors = async () => {
        try {
            const response = await api.get('/sector-activities');
            setSectors(response.data);
        } catch (error) {
            showSnackbar('Erro ao carregar setores', 'error');
            console.error('Error fetching sectors:', error);
        }
    };

    const fetchSubsectors = async () => {
        try {
            setLoading(true);
            const response = await api.get('/type-business/');
            setSubsectors(response.data);
        } catch (error) {
            showSnackbar('Erro ao carregar os Tipo de Negócio', 'error');
            console.error('Error fetching subsectors:', error);
        } finally {
            setLoading(false);
        }
    };

    const handlePageChange = (event, newPage) => {
        setPage(newPage);
    };

    const normalizeStringForSearch = (str) => {
        if (typeof str !== 'string') return '';
        return str
            .normalize("NFD")
            .replace(/[\u0300-\u036f]/g, "")
            .toLowerCase()
            .replace(/\s+/g, '');
    };

    const visibleSubsectors = useMemo(() => {
        const normalizedSearchTerm = normalizeStringForSearch(searchTerm);

        let filteredSubsectors = subsectors.filter(subsector =>
            normalizeStringForSearch(subsector.name).includes(normalizedSearchTerm) ||
            normalizeStringForSearch(subsector.code).includes(normalizedSearchTerm)
        );

        if (sortConfig.key) {
            filteredSubsectors.sort((a, b) => {
                let aValue;
                let bValue;

                if (sortConfig.key === 'sectorCode') {
                    aValue = normalizeStringForSearch(sectors.find(s => s.id === a.sectorActivitId)?.code || '');
                    bValue = normalizeStringForSearch(sectors.find(s => s.id === b.sectorActivitId)?.code || '');
                } else {
                    aValue = normalizeStringForSearch(a[sortConfig.key] || '');
                    bValue = normalizeStringForSearch(b[sortConfig.key] || '');
                }

                if (aValue < bValue) {
                    return sortConfig.direction === 'ascending' ? -1 : 1;
                }
                if (aValue > bValue) {
                    return sortConfig.direction === 'ascending' ? 1 : -1;
                }
                return 0;
            });
        }

        const startIndex = (page - 1) * itemsPerPage;
        const endIndex = startIndex + itemsPerPage;
        return filteredSubsectors.slice(startIndex, endIndex);
    }, [subsectors, page, searchTerm, sortConfig, sectors]);

    const handleOpenDialog = (subsector = null) => {
        setCurrentSubsector(subsector);
        const newFormData = {
            name: subsector ? subsector.name : '',
            code: subsector ? subsector.code : '',
            sectorActivitId: subsector ? subsector.sectorActivitId : null
        };
        setFormData(newFormData);
        setInitialFormData(newFormData);
        setOpenDialog(true);
    };

    const handleCloseDialog = () => {
        setOpenDialog(false);
        setCurrentSubsector(null);
        setFormData({ name: '', code: '', sectorActivitId: null });
        setInitialFormData({ name: '', code: '', sectorActivitId: null });
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        if (name === 'code' && value.length > 3) return;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleSectorChange = (e) => {
        setFormData(prev => ({ ...prev, sectorActivitId: e.target.value }));
    };

    const hasFormChanged = useMemo(() => {
        if (!currentSubsector) return true;
        return (
            formData.name !== initialFormData.name ||
            formData.code !== initialFormData.code ||
            formData.sectorActivitId !== initialFormData.sectorActivitId
        );
    }, [formData, initialFormData, currentSubsector]);

    const handleSubmit = async () => {
        try {
            if (currentSubsector) {
                await api.put(`/type-business/${currentSubsector.id}`, formData);
                showSnackbar('Tipo de negócio atualizado com sucesso', 'success');
            } else {
                await api.post('/type-business/', formData);
                showSnackbar('Tipo de negócio cadastrado com sucesso', 'success');
            }
            fetchSubsectors();
            handleCloseDialog();
        } catch (error) {
            const message = error.response?.data?.error || 'Erro ao processar a solicitação';
            showSnackbar(message, 'error');
            console.error('Error submitting subsector:', error);
        }
    };

    const handleDelete = (id) => {
        setSubsectorToDeleteId(id);
        setOpenConfirmDialog(true);
    };

    const handleConfirmDelete = async () => {
        try {
            await api.delete(`/type-business/${subsectorToDeleteId}`);
            showSnackbar('Tipo de negócio excluído com sucesso', 'success');
            const response = await api.get('/type-business/');
            const updatedSubsectors = response.data;
            setSubsectors(updatedSubsectors);

            const filteredSubsectors = updatedSubsectors.filter(subsector =>
                subsector.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                subsector.code.toLowerCase().includes(searchTerm.toLowerCase())
            );
            const totalPages = Math.ceil(filteredSubsectors.length / itemsPerPage);

            if (page > totalPages && totalPages > 0) {
                setPage(totalPages);
            } else if (totalPages === 0) {
                setPage(1);
            }
        } catch (error) {
            showSnackbar('Erro ao remover o tipo de Negócio', 'error');
            console.error('Error deleting tipo de Negócio:', error);
        } finally {
            setOpenConfirmDialog(false);
            setSubsectorToDeleteId(null);
        }
    };

    const handleCloseConfirmDialog = () => {
        setOpenConfirmDialog(false);
        setSubsectorToDeleteId(null);
    };

    const showSnackbar = (message, severity) => {
        setSnackbar({ open: true, message, severity });
    };

    const handleCloseSnackbar = () => {
        setSnackbar(prev => ({ ...prev, open: false }));
    };

    const handleSearch = (e) => {
        setSearchTerm(e.target.value);
        setPage(1);
    };

    const requestSort = (key) => {
        let direction = 'ascending';
        if (sortConfig.key === key && sortConfig.direction === 'ascending') {
            direction = 'descending';
        }
        setSortConfig({ key, direction });
    };

    return (
        <ThemeProvider theme={theme}>
            <Container maxWidth={isMobile ? 'xs' : 'xl'} sx={{ mt: isMobile ? 1 : 0, mb: 4, px: isMobile ? 1 : 2 }}>
                <Grid container spacing={isMobile ? 1 : 3} sx={{ justifyContent: 'center', mt: 0 }}>
                    <Grid item xs={12} sm={10} md={8}>
                        <Card sx={{
                            borderRadius: isMobile ? '8px' : '12px',
                            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
                            p: isMobile ? 1 : 2
                        }}>
                            <CardContent sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                pt: isMobile ? 0 : 0
                            }}>
                                <Box sx={{
                                    display: 'flex',
                                    flexDirection: isMobile ? 'column' : 'row',
                                    justifyContent: 'space-between',
                                    alignItems: isMobile ? 'center' : 'center',
                                    mb: isMobile ? 2 : 3,
                                    width: '100%',
                                    gap: isMobile ? 2 : 0
                                }}>
                                    <Typography variant={isMobile ? 'h6' : 'h5'} sx={{ color: '#10641c', fontWeight: '600' }}>
                                        Atividades Econômicas
                                    </Typography>
                                    <Button
                                        variant="contained"
                                        startIcon={<AddIcon />}
                                        onClick={() => handleOpenDialog()}
                                        sx={{
                                            bgcolor: '#10641c',
                                            '&:hover': { bgcolor: '#0a3d0f' },
                                            fontSize: isMobile ? '0.9rem' : '0.875rem',
                                            px: isMobile ? 2 : 3
                                        }}
                                    >
                                        Nova Atividade
                                    </Button>
                                </Box>

                                <Divider sx={{ mb: isMobile ? 2 : 2, width: '100%' }} />
                                <Box sx={{ width: '100%', mb: isMobile ? 2 : 2 }}>
                                    <TextField
                                        fullWidth
                                        variant="outlined"
                                        placeholder="Buscar atividades econômicas..."
                                        value={searchTerm}
                                        onChange={handleSearch}
                                        sx={{
                                            height: '45px',
                                            '& .MuiOutlinedInput-root': {
                                                borderRadius: isMobile ? '6px' : '8px',
                                                fontSize: isMobile ? '1rem' : '1rem',
                                                height: 'inherit',
                                                padding: '0 14px',
                                                '& .MuiOutlinedInput-notchedOutline': {
                                                    borderColor: '#ced4da',
                                                },
                                                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                                    borderColor: '#10641c',
                                                },
                                                '& .MuiInputLabel-root.Mui-focused': {
                                                    color: '#10641c',
                                                },
                                                '& .MuiInputBase-input': {
                                                    fontSize: isMobile ? '1rem' : '1rem'
                                                }
                                            }
                                        }}
                                        InputProps={{
                                            startAdornment: (
                                                <InputAdornment position="start">
                                                    <SearchIcon fontSize={isMobile ? 'medium' : 'medium'} />
                                                </InputAdornment>
                                            ),
                                        }}
                                    />
                                </Box>

                                {loading ? (
                                    <Box sx={{ display: 'flex', justifyContent: 'center', p: isMobile ? 2 : 4 }}>
                                        <CircularProgress size={isMobile ? 30 : 40} />
                                    </Box>
                                ) : (
                                    <Box sx={{ width: '100%' }}>
                                        {isMobile ? (
                                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                                {visibleSubsectors.length === 0 ? (
                                                    <Typography sx={{ fontSize: '1rem', color: '#666', textAlign: 'center' }}>
                                                        Nenhuma atividade econômica encontrada.
                                                    </Typography>
                                                ) : (
                                                    visibleSubsectors.map((subsector) => {
                                                        const sector = sectors.find(s => s.id === subsector.sectorActivitId);
                                                        return (
                                                            <Card key={subsector.id} sx={{
                                                                borderRadius: '8px',
                                                                boxShadow: '0 2px 10px rgba(0, 0, 0, 0.1)',
                                                                p: 2
                                                            }}>
                                                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                                                    <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 1 }}>
                                                                        <Typography sx={{ fontSize: '0.9rem', fontWeight: 'bold' }}>
                                                                            Código do Setor:
                                                                        </Typography>
                                                                        <Tooltip title={sector?.name || 'Setor não encontrado'}>
                                                                            <Typography sx={{ fontSize: '0.9rem' }}>
                                                                                {sector?.code || '-'}
                                                                            </Typography>
                                                                        </Tooltip>
                                                                    </Box>
                                                                    <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 1 }}>
                                                                        <Typography sx={{ fontSize: '0.9rem', fontWeight: 'bold' }}>
                                                                            Código da Atividade:
                                                                        </Typography>
                                                                        <Typography sx={{ fontSize: '0.9rem' }}>
                                                                            {subsector.code}
                                                                        </Typography>
                                                                    </Box>
                                                                    <Typography sx={{ fontSize: '0.9rem', fontWeight: 'bold' }}>
                                                                        Nome da Atividade:
                                                                    </Typography>
                                                                    <Typography sx={{ fontSize: '0.9rem' }}>
                                                                        {subsector.name}
                                                                    </Typography>
                                                                    <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, mt: 1 }}>
                                                                        <Tooltip title="Editar">
                                                                            <IconButton
                                                                                onClick={() => handleOpenDialog(subsector)}
                                                                                sx={{ color: '#1976d2', p: 1 }}
                                                                            >
                                                                                <EditIcon fontSize="medium" />
                                                                            </IconButton>
                                                                        </Tooltip>
                                                                        <Tooltip title="Excluir">
                                                                            <IconButton
                                                                                onClick={() => handleDelete(subsector.id)}
                                                                                sx={{ color: '#d32f2f', p: 1 }}
                                                                            >
                                                                                <DeleteIcon fontSize="medium" />
                                                                            </IconButton>
                                                                        </Tooltip>
                                                                    </Box>
                                                                </Box>
                                                            </Card>
                                                        );
                                                    })
                                                )}
                                            </Box>
                                        ) : (
                                            <Box sx={{ width: '100%', overflowX: 'auto' }}>
                                                <TableContainer sx={{ maxWidth: '100%' }}>
                                                    <Table sx={{
                                                        minWidth: 700,
                                                        '& .MuiTableCell-head': { fontWeight: 'bold' },
                                                        '& .MuiTableCell-root': {
                                                            fontSize: '0.875rem',
                                                            p: 1
                                                        }
                                                    }} aria-label="subsetor table">
                                                        <TableHead sx={{ bgcolor: '#f8f8f8' }}>
                                                            <TableRow>
                                                                <TableCell
                                                                    align="center"
                                                                    sx={{ width: '20%', cursor: 'pointer' }}
                                                                    onClick={() => requestSort('sectorCode')}
                                                                >
                                                                    <Box display="flex" alignItems="center" justifyContent="center">
                                                                        Código do Setor
                                                                        {sortConfig.key === 'sectorCode' ? (
                                                                            sortConfig.direction === 'ascending' ?
                                                                                <ArrowUpward fontSize="small" sx={{ ml: 0.5 }} /> :
                                                                                <ArrowDownward fontSize="small" sx={{ ml: 0.5 }} />
                                                                        ) : (
                                                                            <UnfoldMore fontSize="small" color="action" sx={{ ml: 0.5 }} />
                                                                        )}
                                                                    </Box>
                                                                </TableCell>
                                                                <TableCell
                                                                    align="center"
                                                                    sx={{ width: '20%', cursor: 'pointer' }}
                                                                    onClick={() => requestSort('code')}
                                                                >
                                                                    <Box display="flex" alignItems="center" justifyContent="center">
                                                                        Código da Atividade
                                                                        {sortConfig.key === 'code' ? (
                                                                            sortConfig.direction === 'ascending' ?
                                                                                <ArrowUpward fontSize="small" sx={{ ml: 0.5 }} /> :
                                                                                <ArrowDownward fontSize="small" sx={{ ml: 0.5 }} />
                                                                        ) : (
                                                                            <UnfoldMore fontSize="small" color="action" sx={{ ml: 0.5 }} />
                                                                        )}
                                                                    </Box>
                                                                </TableCell>
                                                                <TableCell
                                                                    align="center"
                                                                    sx={{ width: '40%', cursor: 'pointer' }}
                                                                    onClick={() => requestSort('name')}
                                                                >
                                                                    <Box display="flex" alignItems="center" justifyContent="center">
                                                                        Nome da Atividade
                                                                        {sortConfig.key === 'name' ? (
                                                                            sortConfig.direction === 'ascending' ?
                                                                                <ArrowUpward fontSize="small" /> :
                                                                                <ArrowDownward fontSize="small" />
                                                                        ) : (
                                                                            <UnfoldMore fontSize="small" color="action" />
                                                                        )}
                                                                    </Box>
                                                                </TableCell>
                                                                <TableCell
                                                                    align="center"
                                                                    sx={{ width: '20%' }}
                                                                >
                                                                    Ações
                                                                </TableCell>
                                                            </TableRow>
                                                        </TableHead>
                                                        <TableBody>
                                                            {visibleSubsectors.length === 0 ? (
                                                                <TableRow>
                                                                    <TableCell colSpan={4} align="center">
                                                                        <Typography sx={{ fontSize: '0.875rem', color: '#666' }}>
                                                                            Nenhuma atividade econômica encontrada.
                                                                        </Typography>
                                                                    </TableCell>
                                                                </TableRow>
                                                            ) : (
                                                                visibleSubsectors.map((subsector) => {
                                                                    const sector = sectors.find(s => s.id === subsector.sectorActivitId);
                                                                    return (
                                                                        <TableRow key={subsector.id} sx={{ '&:hover': { bgcolor: 'rgba(0, 0, 0, 0.04)' } }}>
                                                                            <TableCell align="center">
                                                                                <Tooltip title={sector?.name || 'Setor não encontrado'}>
                                                                                    <span>{sector?.code || '-'}</span>
                                                                                </Tooltip>
                                                                            </TableCell>
                                                                            <TableCell align="center">{subsector.code}</TableCell>
                                                                            <TableCell align="center">{subsector.name}</TableCell>
                                                                            <TableCell align="center">
                                                                                <Stack direction="row" spacing={1} justifyContent="center">
                                                                                    <Tooltip title="Editar">
                                                                                        <IconButton onClick={() => handleOpenDialog(subsector)} sx={{ color: '#1976d2', p: 1 }}>
                                                                                            <EditIcon fontSize="medium" />
                                                                                        </IconButton>
                                                                                    </Tooltip>
                                                                                    <Tooltip title="Excluir">
                                                                                        <IconButton onClick={() => handleDelete(subsector.id)} sx={{ color: '#d32f2f', p: 1 }}>
                                                                                            <DeleteIcon fontSize="medium" />
                                                                                        </IconButton>
                                                                                    </Tooltip>
                                                                                </Stack>
                                                                            </TableCell>
                                                                        </TableRow>
                                                                    );
                                                                })
                                                            )}
                                                        </TableBody>
                                                    </Table>
                                                </TableContainer>
                                            </Box>
                                        )}
                                    </Box>
                                )}
                            </CardContent>
                        </Card>
                    </Grid>
                </Grid>

                {visibleSubsectors.length > 0 && (
                    <Box sx={{ mt: isMobile ? 1 : 2, display: 'flex', justifyContent: 'center' }}>
                        <Paginate
                            count={Math.ceil(
                                subsectors.filter(subsector =>
                                    subsector.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                                    subsector.code.toLowerCase().includes(searchTerm.toLowerCase())
                                ).length / itemsPerPage
                            )}
                            page={page}
                            onChange={handlePageChange}
                            size={isMobile ? 'small' : 'medium'}
                        />
                    </Box>
                )}

                {/* Dialog para adicionar/editar Tipo de Negócio */}
                <Dialog
                    open={openDialog}
                    onClose={handleCloseDialog}
                    fullWidth
                    maxWidth="xs"
                    fullScreen={isMobile}
                >
                    <DialogTitle
                        sx={{
                            background: 'linear-gradient(45deg, #10641c 30%, #388e3c 90%)',
                            color: 'white',
                            textAlign: 'center'
                        }}
                    >

                        {currentSubsector ? 'Editar Tipo de Negócio' : 'Cadastrar Tipo de Negócio'}

                    </DialogTitle>
                    <DialogContent sx={{ mt: isMobile ? 2 : 2, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                        <Grid container spacing={isMobile ? 1 : 2} sx={{ gap: isMobile ? 2 : 0 }}>
                            <Grid item xs={12}>
                                <TextField
                                    fullWidth
                                    label="Código do Tipo de Negócio"
                                    name="code"
                                    value={formData.code}
                                    onChange={handleChange}
                                    variant="outlined"
                                    inputProps={{ maxLength: 3 }}
                                    sx={{
                                        '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                            borderColor: '#10641c',
                                        },
                                        '& .MuiInputLabel-root.Mui-focused': {
                                            color: '#10641c',
                                        },
                                        '& .MuiInputLabel-root': {

                                            marginTop: '5px',
                                        },
                                        '& .MuiInputBase-input': {
                                            fontSize: isMobile ? '1rem' : '1rem'
                                        }
                                    }}
                                />
                                <TextField
                                    fullWidth
                                    label="Nome do Tipo de Negócio"
                                    name="name"
                                    value={formData.name}
                                    onChange={handleChange}
                                    variant="outlined"
                                    sx={{
                                        marginTop: isMobile ? 2 : 2,
                                        '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                            borderColor: '#10641c',
                                        },
                                        '& .MuiInputLabel-root.Mui-focused': {
                                            color: '#10641c',
                                        },
                                        '& .MuiInputBase-input': {
                                            fontSize: isMobile ? '1rem' : '1rem'
                                        }
                                    }}
                                />
                                <FormControl fullWidth variant="outlined" sx={{ marginTop: isMobile ? 2 : 2 }}>
                                    <InputLabel sx={{
                                        '&.Mui-focused': { color: '#10641c' },
                                        fontSize: isMobile ? '1rem' : '1rem'
                                    }}>
                                        Setor
                                    </InputLabel>
                                    <Select
                                        value={formData.sectorActivitId || ''}
                                        onChange={handleSectorChange}
                                        label="Setor"
                                        sx={{
                                            '& .MuiOutlinedInput-notchedOutline': {
                                                borderColor: formData.sectorActivitId ? '#10641c' : 'inherit',
                                            },
                                            '&:hover .MuiOutlinedInput-notchedOutline': {
                                                borderColor: '#10641c',
                                            },
                                            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                                borderColor: '#10641c',
                                            },
                                            '& .MuiSelect-select': {
                                                fontSize: isMobile ? '1rem' : '1rem'
                                            }
                                        }}
                                        MenuProps={{
                                            PaperProps: {
                                                style: {
                                                    maxHeight: 200,
                                                    fontSize: isMobile ? '1rem' : '1rem'
                                                }
                                            }
                                        }}
                                    >
                                        {sectors
                                            .slice()
                                            .sort((a, b) => a.name.localeCompare(b.name))
                                            .map(sector => (
                                                <MenuItem key={sector.id} value={sector.id} sx={{ fontSize: isMobile ? '1rem' : '1rem' }}>
                                                    {sector.name}
                                                </MenuItem>
                                            ))}
                                    </Select>
                                </FormControl>
                            </Grid>
                        </Grid>
                    </DialogContent>
                    <DialogActions sx={{ p: isMobile ? 2 : 3, justifyContent: 'center', gap: 2 }}>
                        <Button
                            onClick={handleCloseDialog}
                            sx={{
                                bgcolor: 'red',
                                color: 'white',
                                '&:hover': { bgcolor: '#cc0000' },
                                fontSize: isMobile ? '0.9rem' : '0.875rem',
                                px: isMobile ? 2 : 3
                            }}
                            startIcon={<CloseIcon />}
                        >
                            Cancelar
                        </Button>
                        <Button
                            onClick={handleSubmit}
                            variant="contained"
                            disabled={
                                !formData.code.trim() ||
                                !formData.name.trim() ||
                                !formData.sectorActivitId ||
                                (currentSubsector && !hasFormChanged)
                            }
                            sx={{
                                bgcolor: '#10641c',
                                '&:hover': { bgcolor: '#0a3d0f' },
                                fontSize: isMobile ? '0.9rem' : '0.875rem',
                                px: isMobile ? 2 : 3
                            }}
                            startIcon={<SaveIcon />}
                        >
                            {currentSubsector ? 'Atualizar' : 'Salvar'}
                        </Button>
                    </DialogActions>
                </Dialog>

                {/* Diálogo de confirmação de exclusão */}
                <Dialog
                    open={openConfirmDialog}
                    onClose={handleCloseConfirmDialog}
                    fullWidth
                    maxWidth="xs"
                    fullScreen={isMobile}
                >
                    <DialogTitle sx={{ bgcolor: '#d32f2f', color: 'white', textAlign: 'center', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                        Confirmar Exclusão
                    </DialogTitle>
                    <DialogContent sx={{ mt: isMobile ? 1 : 2, textAlign: 'center' }}>
                        <Typography sx={{ fontSize: isMobile ? '1rem' : '1rem' }}>
                            Tem certeza que deseja excluir este Tipo de Negócio? Esta ação é irreversível.
                        </Typography>
                    </DialogContent>
                    <DialogActions sx={{ p: isMobile ? 2 : 3, justifyContent: 'center', gap: 2 }}>
                        <Button
                            onClick={handleCloseConfirmDialog}
                            sx={{
                                bgcolor: '#10641c',
                                color: 'white',
                                '&:hover': { bgcolor: '#0a3d0f' },
                                fontSize: isMobile ? '0.9rem' : '0.875rem',
                                px: isMobile ? 2 : 3
                            }}
                            startIcon={<CloseIcon />}
                        >
                            Cancelar
                        </Button>
                        <Button
                            onClick={handleConfirmDelete}
                            color="error"
                            variant="contained"
                            sx={{
                                fontSize: isMobile ? '0.9rem' : '0.875rem',
                                px: isMobile ? 2 : 3
                            }}
                            startIcon={<DeleteIcon />}
                        >
                            Excluir
                        </Button>
                    </DialogActions>
                </Dialog>

                {/* Snackbar para feedback */}
                <Snackbar
                    open={snackbar.open}
                    autoHideDuration={6000}
                    onClose={handleCloseSnackbar}
                    anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
                >
                    <Alert
                        onClose={handleCloseSnackbar}
                        severity={snackbar.severity}
                        sx={{ width: '100%', fontSize: isMobile ? '0.9rem' : '0.875rem' }}
                    >
                        {snackbar.message}
                    </Alert>
                </Snackbar>
            </Container>
        </ThemeProvider>
    );
};

export default SubsectorPage;